import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { request } from "../../services/api";
import { CircuitBackgroundLayer } from "../../components/CircuitBackground/AnimatedCircuitChipBackground";
import sidebarLogo from "../../images/sidebar-logo-160.png";
import UserMenu from "../../components/UserMenu/UserMenu";

import { LuHardDriveDownload, LuHardDriveUpload } from "react-icons/lu";

import {
  FaThermometerHalf,
  FaTint,
  FaWind,
  FaArrowLeft,
  FaRss,
  FaPhoneAlt,
  FaRedo,
  FaShieldAlt,
  FaCogs,
} from "react-icons/fa";
import styles from "./Monitor.module.css";
import { useMonitorSocket } from "../../hooks/useMonitorSocket";
import SensorGrid from "./SensorGrid";
import ExpandableTile from "../../components/common/ExpandableTile";
import DigitalInputCard from "./DigitalInputCard";
import DigitalOutputCard from "./DigitalOutputCard";
import SensorControlCard from "./SensorControlCard";

import DigitalInputMonitorCard from "./DigitalInputMonitorCard";
import DigitalOutputMonitorCard from "./DigitalOutputMonitorCard";
import SensorMonitorCard from "./SensorMonitorCard";

import SecurityCard from "./SecurityCard";
import GsmControlCard from "./GsmControlCard";
import RemoteControlCard from "./RemoteControlCard";
import { useLocalTheme } from "../../theme/useLocalTheme";

const DEFAULT_MONITOR_TILE_ORDER = ["temp", "humi", "dIn", "dOut", "wind"];

const getMonitorOrderStorageKey = (deviceId) => {
  return `sfarm_monitor_tile_order_${deviceId || "default"}`;
};

const isActiveConnectionValue = (value) => {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true" ||
    value === "TRUE" ||
    value === "on" ||
    value === "ON" ||
    value === "connected"
  );
};

const hasReadableValue = (value) => {
  return (
    value !== undefined &&
    value !== null &&
    value !== "" &&
    value !== "--" &&
    value !== "---" &&
    value !== "N/A" &&
    value !== "nan" &&
    value !== "NaN"
  );
};

const isSensorVisibleForLayout = (data) => {
  if (!data) return false;

  const connected =
    data.connected ?? data.isConnect ?? data.isconnect ?? data.is_connected;

  if (connected !== undefined && connected !== null) {
    return isActiveConnectionValue(connected);
  }

  return (
    hasReadableValue(data.current) ||
    hasReadableValue(data.value) ||
    hasReadableValue(data.status)
  );
};

const isVisibleByUser = (visibilityMap, index) => {
  if (!visibilityMap) return true;

  const value = visibilityMap[index] ?? visibilityMap[String(index)];

  if (value === undefined || value === null) {
    return true;
  }

  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true" ||
    value === "TRUE"
  );
};
const settingsPanelColorClassMap = {
  dIn: styles.settingsPanelDIn,
  dOut: styles.settingsPanelDOut,
  temp: styles.settingsPanelTemp,
  humi: styles.settingsPanelHumi,
  wind: styles.settingsPanelWind,
  security: styles.settingsPanelSecurity,
  gsm: styles.settingsPanelGsm,
  remote: styles.settingsPanelRemote,
};

const getUserVisibleCount = (count, visibilityMap = {}) => {
  let userVisibleCount = 0;

  for (let i = 0; i < count; i++) {
    if (isVisibleByUser(visibilityMap, i)) {
      userVisibleCount++;
    }
  }

  return userVisibleCount;
};

const getVisibleSensorCount = (
  sensorData,
  suspendedSensors,
  type,
  count,
  visibilityMap = {},
) => {
  let visibleCount = 0;

  for (let i = 0; i < count; i++) {
    if (!isVisibleByUser(visibilityMap, i)) {
      continue;
    }

    const key = `${type}${i}`;

    if (suspendedSensors?.has?.(key)) {
      continue;
    }

    const data = sensorData?.[key];

    if (isSensorVisibleForLayout(data)) {
      visibleCount++;
    }
  }

  return visibleCount;
};

const getFilteredSensorDataByVisibility = (
  sensorData,
  type,
  count,
  visibilityMap = {},
) => {
  if (!sensorData) return {};

  const filteredData = { ...sensorData };

  for (let i = 0; i < count; i++) {
    if (!isVisibleByUser(visibilityMap, i)) {
      delete filteredData[`${type}${i}`];
    }
  }

  return filteredData;
};

const getSmartTileSpan = (visibleCount) => {
  if (visibleCount <= 2) return 1;
  if (visibleCount <= 4) return 2;
  return 4;
};
const getSmartTileWidth = (visibleCount) => {
  const count = Math.max(1, visibleCount);

  const cardWidth = 145;
  const cardGap = 10;
  const tilePadding = 38;
  const minTileWidth = 220;
  const maxTileWidth = 1280;

  const calculatedWidth =
    count * cardWidth + Math.max(0, count - 1) * cardGap + tilePadding;

  return Math.min(maxTileWidth, Math.max(minTileWidth, calculatedWidth));
};

const normalizePermissionValue = (value) => {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true" ||
    value === "TRUE"
  );
};

const hasPermissionPayload = (permissions) => {
  return (
    permissions &&
    typeof permissions === "object" &&
    Object.keys(permissions).length > 0
  );
};

const readJsonFromStorage = (storage, key) => {
  try {
    const rawValue = storage.getItem(key);

    if (!rawValue) return null;

    const parsedValue = JSON.parse(rawValue);

    return hasPermissionPayload(parsedValue) ? parsedValue : null;
  } catch {
    return null;
  }
};

const MonitorPage = () => {
  useLocalTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");

  const getInitialDevicePermissions = () => {
    const statePermissions =
      location.state?.permissions || location.state?.device?.permissions;

    if (hasPermissionPayload(statePermissions)) {
      sessionStorage.setItem(
        `device_permissions_${id}`,
        JSON.stringify(statePermissions),
      );

      return statePermissions;
    }

    return readJsonFromStorage(sessionStorage, `device_permissions_${id}`);
  };

  const [devicePermissions, setDevicePermissions] = useState(
    getInitialDevicePermissions,
  );

  const [isPermissionLoading, setIsPermissionLoading] = useState(
    () => !getInitialDevicePermissions(),
  );
  const [isSettingsSidebarOpen, setIsSettingsSidebarOpen] = useState(() => {
    return localStorage.getItem("sfarm_settings_sidebar_open") !== "0";
  });

  const toggleSettingsSidebar = () => {
    setIsSettingsSidebarOpen((prev) => {
      const nextValue = !prev;
      localStorage.setItem(
        "sfarm_settings_sidebar_open",
        nextValue ? "1" : "0",
      );
      return nextValue;
    });
  };

  useEffect(() => {
    let cancelled = false;

    const loadDevicePermissions = async () => {
      const cachedPermissions = readJsonFromStorage(
        sessionStorage,
        `device_permissions_${id}`,
      );

      if (cachedPermissions) {
        setDevicePermissions(cachedPermissions);
        setIsPermissionLoading(false);
        return;
      }

      setIsPermissionLoading(true);

      try {
        const data = await request("/get_devices.php");

        if (cancelled) return;

        if (data?.status !== "success" || !Array.isArray(data.devices)) {
          setDevicePermissions(null);
          return;
        }

        const currentDevice = data.devices.find(
          (item) => String(item.id) === String(id),
        );

        if (!currentDevice?.permissions) {
          setDevicePermissions(null);
          return;
        }

        sessionStorage.setItem(
          `device_permissions_${id}`,
          JSON.stringify(currentDevice.permissions),
        );

        sessionStorage.setItem(`device_${id}`, JSON.stringify(currentDevice));

        setDevicePermissions(currentDevice.permissions);
      } catch (error) {
        console.error("Error loading device permissions:", error);

        if (!cancelled) {
          setDevicePermissions(null);
        }
      } finally {
        if (!cancelled) {
          setIsPermissionLoading(false);
        }
      }
    };

    if (id) {
      loadDevicePermissions();
    }

    return () => {
      cancelled = true;
    };
  }, [id]);

  const [customNames, setCustomNames] = useState({
    dIn: {},
    dOut: {},
    temp: {},
    humi: {},
    wind: {},
  });

  const [visibilitySettings, setVisibilitySettings] = useState({
    dIn: {},
    dOut: {},
    temp: {},
    humi: {},
    wind: {},
  });

  const [monitorTileOrder, setMonitorTileOrder] = useState(
    DEFAULT_MONITOR_TILE_ORDER,
  );
  const [activeSettingsPanel, setActiveSettingsPanel] = useState(null);

  const {
    device,
    sensorData,
    wsConnected,
    suspendedSensors,
    sendCommand,
    handleRetryAll,
  } = useMonitorSocket(id);

  const canMonitor = normalizePermissionValue(devicePermissions?.can_monitor);
  const canConfigure = normalizePermissionValue(
    devicePermissions?.can_configure,
  );
  const canControlOutputs = normalizePermissionValue(
    devicePermissions?.can_control_outputs,
  );
  const canManageUsers = normalizePermissionValue(
    devicePermissions?.can_manage_users,
  );
  const isDeviceOwner = normalizePermissionValue(devicePermissions?.is_owner);

  useEffect(() => {
    let cancelled = false;

    const loadAllCustomNames = async () => {
      try {
        const [dIn, dOut, temp, humi, wind] = await Promise.all([
          request(`/manage_input_names.php?device_id=${id}&input_type=dIn`),
          request(`/manage_input_names.php?device_id=${id}&input_type=dOut`),
          request(`/manage_input_names.php?device_id=${id}&input_type=temp`),
          request(`/manage_input_names.php?device_id=${id}&input_type=humi`),
          request(`/manage_input_names.php?device_id=${id}&input_type=wind`),
        ]);

        if (cancelled) return;

        setCustomNames({
          dIn: dIn || {},
          dOut: dOut || {},
          temp: temp || {},
          humi: humi || {},
          wind: wind || {},
        });
      } catch (error) {
        console.error("Error loading custom names:", error);
      }
    };

    if (id) {
      loadAllCustomNames();
    }

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const loadAllVisibilitySettings = async () => {
      try {
        const [dIn, dOut, temp, humi, wind] = await Promise.all([
          request(
            `/manage_input_visibility.php?device_id=${id}&input_type=dIn`,
          ),
          request(
            `/manage_input_visibility.php?device_id=${id}&input_type=dOut`,
          ),
          request(
            `/manage_input_visibility.php?device_id=${id}&input_type=temp`,
          ),
          request(
            `/manage_input_visibility.php?device_id=${id}&input_type=humi`,
          ),
          request(
            `/manage_input_visibility.php?device_id=${id}&input_type=wind`,
          ),
        ]);

        if (cancelled) return;

        setVisibilitySettings({
          dIn: dIn || {},
          dOut: dOut || {},
          temp: temp || {},
          humi: humi || {},
          wind: wind || {},
        });
      } catch (error) {
        console.error("Error loading visibility settings:", error);
      }
    };

    if (id) {
      loadAllVisibilitySettings();
    }

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    const storageKey = getMonitorOrderStorageKey(id);

    try {
      const savedOrder = localStorage.getItem(storageKey);

      if (!savedOrder) {
        setMonitorTileOrder(DEFAULT_MONITOR_TILE_ORDER);
        return;
      }

      const parsedOrder = JSON.parse(savedOrder);

      if (!Array.isArray(parsedOrder)) {
        setMonitorTileOrder(DEFAULT_MONITOR_TILE_ORDER);
        return;
      }

      const validOrder = parsedOrder.filter((item) =>
        DEFAULT_MONITOR_TILE_ORDER.includes(item),
      );

      const missingItems = DEFAULT_MONITOR_TILE_ORDER.filter(
        (item) => !validOrder.includes(item),
      );

      setMonitorTileOrder([...validOrder, ...missingItems]);
    } catch (error) {
      console.error("Error loading monitor tile order:", error);
      setMonitorTileOrder(DEFAULT_MONITOR_TILE_ORDER);
    }
  }, [id]);

  const handleSaveCustomName = async (type, index, newName) => {
    if (!canConfigure) {
      alert("شما اجازه تغییر تنظیمات این دستگاه را ندارید.");
      return;
    }

    try {
      await request("/manage_input_names.php", "POST", {
        device_id: id,
        input_type: type,
        input_index: index,
        customName: newName,
      });

      setCustomNames((prev) => ({
        ...prev,
        [type]: {
          ...prev[type],
          [index]: newName,
        },
      }));
    } catch (error) {
      console.error("Error saving custom name:", error);
    }
  };

  const handleSaveVisibility = async (type, index, isVisible) => {
    if (!canConfigure) {
      alert("شما اجازه تغییر تنظیمات نمایش این دستگاه را ندارید.");
      return;
    }

    const inputIndex = Number(index);

    console.log("SAVE VISIBILITY:", {
      type,
      index,
      inputIndex,
      isVisible,
    });

    if (!type || !Number.isInteger(inputIndex)) {
      console.error("شماره آیتم نامعتبر است:", {
        type,
        index,
        isVisible,
      });

      alert("شماره سنسور / ورودی نامعتبر است");
      return;
    }

    try {
      await request("/manage_input_visibility.php", "POST", {
        device_id: id,
        input_type: type,
        input_index: inputIndex,
        is_visible: isVisible ? 1 : 0,
      });

      setVisibilitySettings((prev) => ({
        ...prev,
        [type]: {
          ...(prev[type] || {}),
          [inputIndex]: isVisible,
        },
      }));
    } catch (error) {
      console.error("خطا در ذخیره تنظیمات نمایش:", error);
      alert("خطا در ذخیره تنظیمات نمایش");
      throw error;
    }
  };

  const openSettingsPanel = (panelName) => {
    if (!canConfigure) return;

    setActiveSettingsPanel(panelName);
  };

  const closeSettingsPanel = () => {
    setActiveSettingsPanel(null);
  };

  useEffect(() => {
    if (!canConfigure && activeSettingsPanel) {
      setActiveSettingsPanel(null);
    }
  }, [canConfigure, activeSettingsPanel]);

  if (!device || isPermissionLoading) {
    return (
      <div className={styles.container}>
        <CircuitBackgroundLayer />
        <div className={styles.overlay}></div>

        <div
          className={styles.content}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          در حال بارگذاری...
        </div>
      </div>
    );
  }

  if (!canMonitor) {
    return (
      <div className={styles.container}>
        <CircuitBackgroundLayer />
        <div className={styles.overlay}></div>

        <div
          className={styles.content}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: "14px",
            color: "#fff",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "1.1rem", fontWeight: "bold" }}>
            شما اجازه مشاهده این دستگاه را ندارید.
          </div>

          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className={`${styles.headerRoundBtn} ${styles.backBtn}`}
            aria-label="بازگشت به داشبورد"
          >
            <FaArrowLeft />
          </button>
        </div>
      </div>
    );
  }

  const safeSensorData = sensorData || {};
  const safeSuspendedSensors = suspendedSensors || new Set();

  const monitorSections = [
    {
      id: "dIn",
      title: "ورودی‌های دیجیتال",
      icon: <LuHardDriveDownload color="#f6ad55" />,
      type: "dIn",
      count: 68,
      label: "ورودی",
      unit: "",
      CardComponent: DigitalInputMonitorCard,
      customNames: customNames.dIn,
      visibilitySettings: visibilitySettings.dIn,
    },
    {
      id: "dOut",
      title: "کنترل خروجی‌ها",
      icon: <LuHardDriveUpload color="#39ff14" />,
      type: "dOut",
      count: 72,
      label: "خروجی",
      unit: "",
      CardComponent: DigitalOutputMonitorCard,
      customNames: customNames.dOut,
      visibilitySettings: visibilitySettings.dOut,
    },
    {
      id: "temp",
      title: "کنترل دما",
      icon: <FaThermometerHalf color="#ff6b6b" />,
      type: "temp",
      count: 9,
      label: "دما",
      unit: "°C",
      CardComponent: SensorMonitorCard,
      customNames: customNames.temp,
      visibilitySettings: visibilitySettings.temp,
    },
    {
      id: "humi",
      title: "کنترل رطوبت",
      icon: <FaTint color="#4dabf7" />,
      type: "humi",
      count: 9,
      label: "رطوبت",
      unit: "%",
      CardComponent: SensorMonitorCard,
      customNames: customNames.humi,
      visibilitySettings: visibilitySettings.humi,
    },
    {
      id: "wind",
      title: "سنجش باد",
      icon: <FaWind color="#cbd5e0" />,
      type: "wind",
      count: 1,
      label: "باد",
      unit: "km/h",
      CardComponent: SensorMonitorCard,
      customNames: customNames.wind,
      visibilitySettings: visibilitySettings.wind,
    },
  ];

  const orderedMonitorSections = monitorTileOrder
    .map((sectionId) =>
      monitorSections.find((section) => section.id === sectionId),
    )
    .filter(Boolean)
    .map((section) => {
      const visibleCount = getVisibleSensorCount(
        safeSensorData,
        safeSuspendedSensors,
        section.type,
        section.count,
        section.visibilitySettings,
      );

      const userVisibleCount = getUserVisibleCount(
        section.count,
        section.visibilitySettings,
      );

      const tileVisibleCount = Math.max(1, visibleCount);

      return {
        ...section,
        visibleCount,
        userVisibleCount,
        tileVisibleCount,
        tileWidth: getSmartTileWidth(tileVisibleCount),
      };
    })
    .filter((section) => section.visibleCount > 0);

  const renderSettingsPanelTitle = () => {
    switch (activeSettingsPanel) {
      case "dIn":
        return "تنظیمات ورودی‌های دیجیتال";
      case "dOut":
        return "تنظیمات خروجی‌ها";
      case "temp":
        return "تنظیمات دما";
      case "humi":
        return "تنظیمات رطوبت";
      case "wind":
        return "تنظیمات باد";
      case "security":
        return "امنیت و حفاظت";
      case "gsm":
        return "تنظیمات GSM";
      case "remote":
        return "ریموت کنترلر";
      default:
        return "تنظیمات";
    }
  };

  const renderSettingsPanelContent = () => {
    if (!canConfigure) {
      return (
        <div className={styles.emptySection}>
          شما اجازه تغییر تنظیمات این دستگاه را ندارید.
        </div>
      );
    }

    switch (activeSettingsPanel) {
      case "dIn":
        return (
          <SensorGrid
            type="dIn"
            count={68}
            label="ورودی"
            icon={<LuHardDriveDownload color="#f6ad55" />}
            unit=""
            sensorData={safeSensorData}
            suspendedSensors={safeSuspendedSensors}
            onUpdate={sendCommand}
            CardComponent={DigitalInputCard}
            customNames={customNames.dIn}
            onSaveCustomName={(index, name) =>
              handleSaveCustomName("dIn", index, name)
            }
            visibilitySettings={visibilitySettings.dIn}
            showVisibilityToggle={true}
            filterByVisibility={false}
            onSaveVisibility={handleSaveVisibility}
            canConfigure={canConfigure}
            canControlOutputs={canControlOutputs}
          />
        );

      case "dOut":
        return (
          <SensorGrid
            type="dOut"
            count={72}
            label="خروجی"
            icon={<LuHardDriveUpload color="#39ff14" />}
            unit=""
            sensorData={safeSensorData}
            suspendedSensors={safeSuspendedSensors}
            onUpdate={sendCommand}
            CardComponent={DigitalOutputCard}
            customNames={customNames.dOut}
            allCustomNames={customNames}
            onSaveCustomName={(index, name) =>
              handleSaveCustomName("dOut", index, name)
            }
            visibilitySettings={visibilitySettings.dOut}
            showVisibilityToggle={true}
            filterByVisibility={false}
            onSaveVisibility={handleSaveVisibility}
            canConfigure={canConfigure}
            canControlOutputs={canControlOutputs}
          />
        );

      case "temp":
        return (
          <SensorGrid
            type="temp"
            count={9}
            label="دما"
            icon={<FaThermometerHalf color="#ff6b6b" />}
            unit="°C"
            sensorData={safeSensorData}
            suspendedSensors={safeSuspendedSensors}
            onUpdate={sendCommand}
            CardComponent={SensorControlCard}
            customNames={customNames.temp}
            onSaveCustomName={(index, name) =>
              handleSaveCustomName("temp", index, name)
            }
            serial={device.device_serial}
            token={token}
            visibilitySettings={visibilitySettings.temp}
            showVisibilityToggle={true}
            onSaveVisibility={handleSaveVisibility}
            canConfigure={canConfigure}
            canControlOutputs={canControlOutputs}
          />
        );

      case "humi":
        return (
          <SensorGrid
            type="humi"
            count={9}
            label="رطوبت"
            icon={<FaTint color="#4dabf7" />}
            unit="%"
            sensorData={safeSensorData}
            suspendedSensors={safeSuspendedSensors}
            onUpdate={sendCommand}
            CardComponent={SensorControlCard}
            customNames={customNames.humi}
            onSaveCustomName={(index, name) =>
              handleSaveCustomName("humi", index, name)
            }
            serial={device.device_serial}
            token={token}
            visibilitySettings={visibilitySettings.humi}
            showVisibilityToggle={true}
            onSaveVisibility={handleSaveVisibility}
            canConfigure={canConfigure}
            canControlOutputs={canControlOutputs}
          />
        );

      case "wind":
        return (
          <SensorGrid
            type="wind"
            count={1}
            label="باد"
            icon={<FaWind color="#cbd5e0" />}
            unit="km/h"
            sensorData={safeSensorData}
            suspendedSensors={safeSuspendedSensors}
            onUpdate={sendCommand}
            CardComponent={SensorControlCard}
            customNames={customNames.wind}
            onSaveCustomName={(index, name) =>
              handleSaveCustomName("wind", index, name)
            }
            serial={device.device_serial}
            token={token}
            visibilitySettings={visibilitySettings.wind}
            showVisibilityToggle={true}
            onSaveVisibility={(index, isVisible) =>
              handleSaveVisibility("wind", index, isVisible)
            }
          />
        );

      case "security":
        return (
          <div className={styles.sensorsGrid}>
            {safeSensorData["secu0"] ? (
              <SecurityCard
                data={safeSensorData["secu0"]}
                onUpdate={
                  canConfigure
                    ? (prop, val) => sendCommand("secu", 0, prop, val)
                    : undefined
                }
              />
            ) : (
              <div className={styles.emptySection}>
                اطلاعات امنیت و حفاظت هنوز دریافت نشده است.
              </div>
            )}
          </div>
        );

      case "gsm":
        return (
          <div className={styles.sensorsGrid}>
            {safeSensorData["gsm0"] ? (
              <GsmControlCard
                data={safeSensorData["gsm0"]}
                onUpdate={
                  canConfigure
                    ? (prop, val) => sendCommand("gsm", 0, prop, val)
                    : undefined
                }
              />
            ) : (
              <div className={styles.emptySection}>
                اطلاعات GSM هنوز دریافت نشده است.
              </div>
            )}
          </div>
        );

      case "remote":
        return (
          <div className={styles.sensorsGrid}>
            {safeSensorData["remote0"] ? (
              <RemoteControlCard
                data={safeSensorData["remote0"]}
                onUpdate={
                  canConfigure
                    ? (prop, val) => sendCommand("remote", 0, prop, val)
                    : undefined
                }
              />
            ) : (
              <div className={styles.emptySection}>
                اطلاعات ریموت کنترلر هنوز دریافت نشده است.
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <CircuitBackgroundLayer />
      <div className={styles.overlay}></div>

      <div
        className={`
        ${styles.content}
        ${
          canConfigure
            ? isSettingsSidebarOpen
              ? styles.contentWithSidebar
              : styles.contentWithCollapsedSidebar
            : ""
        }
    `}
      >
        <div className={styles.header}>
          <div className={styles.deviceTitle}>
            <span style={{ fontSize: "2rem" }}>🏭</span>

            <div>
              <div style={{ fontWeight: "bold" }}>{device.device_name}</div>

              <span
                style={{
                  fontSize: "12px",
                  color: "#ccc",
                  fontFamily: "arial",
                }}
              >
                {device.device_serial}
              </span>
            </div>
          </div>

          <div className={styles.headerActions}>
            {safeSuspendedSensors.size > 0 && (
              <div
                className={styles.headerActionHint}
                data-hint={`بازیابی (${safeSuspendedSensors.size})`}
              >
                <button
                  type="button"
                  onClick={handleRetryAll}
                  className={`${styles.headerRoundBtn} ${styles.retryBtn}`}
                  aria-label={`بازیابی ${safeSuspendedSensors.size} مورد`}
                >
                  <FaRedo />
                </button>
              </div>
            )}

            <div className={styles.headerUserMenuSlot}>
              <UserMenu dropdownAlign="left" />
            </div>
            <div className={styles.headerActionHint} data-hint="بازگشت">
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className={`${styles.headerRoundBtn} ${styles.backBtn}`}
                aria-label="بازگشت"
              >
                <FaArrowLeft />
              </button>
            </div>
          </div>
        </div>
        <div className={styles.monitorTilesGrid}>
          {orderedMonitorSections.map((section) => (
            <ExpandableTile
              key={section.id}
              title={section.title}
              icon={section.icon}
              defaultExpanded={true}
              className={styles.smartMonitorTile}
              style={{
                "--tile-width": `${section.tileWidth}px`,
                "--tile-visible-count": section.tileVisibleCount,
              }}
            >
              <SensorGrid
                type={section.type}
                count={section.count}
                label={section.label}
                icon={section.icon}
                unit={section.unit}
                sensorData={getFilteredSensorDataByVisibility(
                  safeSensorData,
                  section.type,
                  section.count,
                  section.visibilitySettings,
                )}
                suspendedSensors={safeSuspendedSensors}
                onUpdate={sendCommand}
                CardComponent={section.CardComponent}
                customNames={section.customNames}
                allCustomNames={customNames}
                onSaveCustomName={(index, name) =>
                  handleSaveCustomName(section.type, index, name)
                }
                serial={device.device_serial}
                token={token}
                visibilitySettings={section.visibilitySettings}
                filterByVisibility={true}
                canConfigure={canConfigure}
                canControlOutputs={canControlOutputs}
              />
            </ExpandableTile>
          ))}
        </div>
      </div>

      {canConfigure && (
        <aside
          className={`
      ${styles.settingsSidebar}
      ${styles.settingsFocusSurface}
      ${!isSettingsSidebarOpen ? styles.settingsSidebarCollapsed : ""}
    `}
        >
          <div
            className={styles.sidebarStatusBox}
            title={wsConnected ? "دستگاه آنلاین است" : "دستگاه آفلاین است"}
          >
            <span
              className={`
          ${styles.sidebarStatusDot}
          ${wsConnected ? styles.sidebarStatusOnline : styles.sidebarStatusOffline}
      `}
            ></span>

            <span className={styles.sidebarStatusText}>
              {wsConnected ? "آنلاین" : "آفلاین"}
            </span>
          </div>

          <button
            type="button"
            className={styles.sidebarCollapseBtn}
            onClick={toggleSettingsSidebar}
            title={isSettingsSidebarOpen ? "جمع کردن منو" : "باز کردن منو"}
          >
            {isSettingsSidebarOpen ? "›" : "‹"}
          </button>

          <nav className={styles.sidebarNav}>
            <button
              type="button"
              className={`${styles.sidebarNavItem} ${styles.sidebarNavDIn}`}
              onClick={() => openSettingsPanel("dIn")}
              title="ورودی‌ها"
            >
              <span className={styles.sidebarNavIcon}>
                <LuHardDriveDownload />
              </span>
              <span className={styles.sidebarNavText}>ورودی‌ها</span>
            </button>

            <button
              type="button"
              className={`${styles.sidebarNavItem} ${styles.sidebarNavDOut}`}
              onClick={() => openSettingsPanel("dOut")}
              title="خروجی‌ها"
            >
              <span className={styles.sidebarNavIcon}>
                <LuHardDriveUpload />
              </span>
              <span className={styles.sidebarNavText}>خروجی‌ها</span>
            </button>

            <button
              type="button"
              className={`${styles.sidebarNavItem} ${styles.sidebarNavTemp}`}
              onClick={() => openSettingsPanel("temp")}
              title="دما"
            >
              <span className={styles.sidebarNavIcon}>
                <FaThermometerHalf />
              </span>
              <span className={styles.sidebarNavText}>دما</span>
            </button>

            <button
              type="button"
              className={`${styles.sidebarNavItem} ${styles.sidebarNavHumi}`}
              onClick={() => openSettingsPanel("humi")}
              title="رطوبت"
            >
              <span className={styles.sidebarNavIcon}>
                <FaTint />
              </span>
              <span className={styles.sidebarNavText}>رطوبت</span>
            </button>

            <button
              type="button"
              className={`${styles.sidebarNavItem} ${styles.sidebarNavWind}`}
              onClick={() => openSettingsPanel("wind")}
              title="باد"
            >
              <span className={styles.sidebarNavIcon}>
                <FaWind />
              </span>
              <span className={styles.sidebarNavText}>باد</span>
            </button>

            <button
              type="button"
              className={`${styles.sidebarNavItem} ${styles.sidebarNavSecurity}`}
              onClick={() => openSettingsPanel("security")}
              title="امنیت"
            >
              <span className={styles.sidebarNavIcon}>
                <FaShieldAlt />
              </span>
              <span className={styles.sidebarNavText}>امنیت</span>
            </button>

            <button
              type="button"
              className={`${styles.sidebarNavItem} ${styles.sidebarNavGsm}`}
              onClick={() => openSettingsPanel("gsm")}
              title="GSM"
            >
              <span className={styles.sidebarNavIcon}>
                <FaPhoneAlt />
              </span>
              <span className={styles.sidebarNavText}>GSM</span>
            </button>

            <button
              type="button"
              className={`${styles.sidebarNavItem} ${styles.sidebarNavRemote}`}
              onClick={() => openSettingsPanel("remote")}
              title="ریموت"
            >
              <span className={styles.sidebarNavIcon}>
                <FaRss />
              </span>
              <span className={styles.sidebarNavText}>ریموت</span>
            </button>
          </nav>
          <button
            type="button"
            className={styles.sidebarBrandFooter}
            onClick={() => navigate("/dashboard")}
            title="رفتن به صفحه اصلی"
          >
            <div className={styles.sidebarLogo}>
              <img
                src={sidebarLogo}
                alt="Smart Farm Logo"
                className={styles.sidebarLogoImg}
              />
            </div>

            <div className={styles.sidebarBrandText}>
              <span className={styles.sidebarBrandName}>Smart Farm</span>

              <span className={styles.sidebarBrandSub}>Monitoring</span>
            </div>
          </button>
        </aside>
      )}

      {canConfigure && activeSettingsPanel && (
        <div
          className={styles.settingsDrawerOverlay}
          onClick={closeSettingsPanel}
        >
          <div
            className={`
        ${styles.settingsDrawer}
        ${styles.settingsFocusSurface}
        ${settingsPanelColorClassMap[activeSettingsPanel] || ""}
    `}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.settingsDrawerHeader}>
              <span>
                <FaCogs style={{ marginLeft: "8px" }} />
                {renderSettingsPanelTitle()}
              </span>

              <button
                type="button"
                className={styles.settingsDrawerClose}
                onClick={closeSettingsPanel}
              >
                ×
              </button>
            </div>

            <div className={styles.settingsDrawerBody}>
              {renderSettingsPanelContent()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitorPage;
